/**
 * Configuration → Internal Rates hooks (2026-10-14, docs/specs/resource-internal-rates.md).
 *
 * Every request carries the step-up proof as `X-Step-Up`. The proof id lives in the
 * page's React state only — never in storage — so leaving or refreshing the page asks
 * again. A 403 with `code: "step_up_required"` (expired proof) surfaces as
 * `StepUpRequiredError`, which the page answers by asking again.
 *
 * Responses are parsed with the Zod schemas in @/schemas/resource-internal-rates, not cast.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { z } from "zod";
import { internalRateKeys, stepUpKeys } from "@/lib/query";
import {
  type AddInternalRateInput,
  apiErrorSchema,
  internalRateHistorySchema,
  resourceInternalRatePageSchema,
  stepUpGrantSchema,
  stepUpOptionsSchema,
} from "@/schemas/resource-internal-rates";

export class StepUpRequiredError extends Error {
  constructor(message = "Confirm it's you to continue.") {
    super(message);
    this.name = "StepUpRequiredError";
  }
}

async function request<S extends z.ZodType>(
  schema: S,
  url: string,
  init: RequestInit & { stepUpId?: string } = {},
): Promise<z.infer<S>> {
  const { stepUpId, headers, ...rest } = init;
  const res = await fetch(url, {
    ...rest,
    headers: {
      ...(rest.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(stepUpId === undefined ? {} : { "X-Step-Up": stepUpId }),
      ...headers,
    },
  });
  const body: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = apiErrorSchema.safeParse(body);
    const message = err.success ? err.data.error : undefined;
    if (res.status === 403 && err.success && err.data.code === "step_up_required") {
      throw new StepUpRequiredError(message);
    }
    throw new Error(message ?? `Request failed (${res.status})`);
  }
  return schema.parse(body);
}

// ── Step-up ───────────────────────────────────────────────────────────────────

export function useStepUpOptions() {
  return useQuery({
    queryKey: stepUpKeys.options(),
    queryFn: () => request(stepUpOptionsSchema, "/api/step-up/options"),
  });
}

/** Re-enter the password. Resolves to the proof id. */
export function usePasswordStepUp() {
  return useMutation({
    mutationFn: (password: string) =>
      request(stepUpGrantSchema, "/api/step-up/password", {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Claim a proof for the session the user just made by signing in again through SSO. */
export function useSsoStepUp() {
  return useMutation({
    mutationFn: () => request(stepUpGrantSchema, "/api/step-up/sso", { method: "POST" }),
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Rates ─────────────────────────────────────────────────────────────────────

export function useResourceInternalRates(
  stepUpId: string,
  params: { search: string; withoutRate: boolean; cursor: string | null },
) {
  const qs = new URLSearchParams();
  if (params.search.length > 0) qs.set("search", params.search);
  if (params.withoutRate) qs.set("withoutRate", "true");
  if (params.cursor !== null) qs.set("cursor", params.cursor);
  return useQuery({
    queryKey: internalRateKeys.list(stepUpId, params),
    queryFn: () =>
      request(resourceInternalRatePageSchema, `/api/resource-internal-rates?${qs.toString()}`, {
        stepUpId,
      }),
    placeholderData: keepPreviousData,
    retry: (count, err) => !(err instanceof StepUpRequiredError) && count < 2,
  });
}

/** Fetched fresh whenever the history drawer opens (frontend.md — dialogs use `enabled`). */
export function useResourceInternalRateHistory(
  stepUpId: string,
  resourceId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: internalRateKeys.history(stepUpId, resourceId),
    queryFn: () =>
      request(
        internalRateHistorySchema,
        `/api/resource-internal-rates/${encodeURIComponent(resourceId)}/history`,
        { stepUpId },
      ),
    enabled,
    retry: (count, err) => !(err instanceof StepUpRequiredError) && count < 2,
  });
}

export function useAddResourceInternalRate(stepUpId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddInternalRateInput) =>
      request(internalRateHistorySchema, "/api/resource-internal-rates", {
        method: "POST",
        body: JSON.stringify(input),
        stepUpId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: internalRateKeys.all });
      toast.success("Internal Rate saved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveResourceInternalRate(stepUpId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { rateId: string; reason: string }) =>
      request(
        internalRateHistorySchema,
        `/api/resource-internal-rates/${encodeURIComponent(input.rateId)}`,
        { method: "DELETE", body: JSON.stringify({ reason: input.reason }), stepUpId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: internalRateKeys.all });
      toast.success("Internal Rate removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
